/* Hero bottle → featured product fly-through.
 *
 * Clones the hero bottle into a fixed-positioned "ghost" that's lerped between
 * the hero bottle's bounding box and the featured-product bottle's bounding box
 * as the user scrolls. The two real <img> elements stay in the DOM (preserves
 * layout) but are held at opacity:0 — the ghost is what the user sees the whole
 * way through. Hero copy fades out as the bottle leaves; product chrome fades
 * in as it arrives. Reverses on scroll up.
 *
 * Loaded only on the index template (see layout/theme.liquid). Skipped if GSAP
 * isn't present, in Shopify's design-mode preview, or if the user prefers
 * reduced motion.
 */
(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function init() {
    if (!window.gsap || !window.ScrollTrigger) return;

    const heroBottle = document.querySelector('.hero-video__bottle img');
    const productSection = document.querySelector('[data-wetheme-section-type="featured-product"]');
    if (!heroBottle || !productSection) return;

    const productBottle = pickVisibleImage(
      productSection.querySelectorAll('[data-product-media-wrapper] img.theme-img')
    );
    if (!productBottle) return;

    Promise.all([waitForImage(heroBottle), waitForImage(productBottle)]).then(() => {
      setup(heroBottle, productBottle, productSection);
    });
  }

  function pickVisibleImage(nodeList) {
    for (let i = 0; i < nodeList.length; i++) {
      const r = nodeList[i].getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return nodeList[i];
    }
    return nodeList[0] || null;
  }

  function waitForImage(img) {
    return new Promise(function (resolve) {
      if (img.complete && img.naturalWidth !== 0) return resolve();
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  function setup(heroBottle, productBottle, productSection) {
    gsap.registerPlugin(ScrollTrigger);

    // Mobile browsers fire `resize` every time the address bar shows/hides.
    // Tell ScrollTrigger to ignore those so it doesn't refresh mid-scroll.
    ScrollTrigger.config({ ignoreMobileResize: true });

    // Build the ghost (visible stand-in)
    const ghost = heroBottle.cloneNode(true);
    ghost.removeAttribute('id');
    ghost.removeAttribute('data-animate');
    ghost.removeAttribute('data-animate-order');
    // Strip the responsive-image snippet's inline styles (object-fit: cover,
    // natural-width caps, focal-point overrides) — ghost manages its own box.
    ghost.removeAttribute('style');
    ghost.classList.add('bottle-ghost');
    Object.assign(ghost.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      margin: '0',
      padding: '0',
      maxWidth: 'none',
      maxHeight: 'none',
      objectFit: 'contain',
      objectPosition: '50% 50%',
      pointerEvents: 'none',
      zIndex: '5',
      transformOrigin: 'top left',
      willChange: 'transform, width, height',
      display: 'block',
    });
    document.body.appendChild(ghost);

    // Hide the originals — ghost holds the visual the entire time
    heroBottle.style.opacity = '0';
    productBottle.style.opacity = '0';

    // State: while the trigger is active the ghost is the visible bottle and
    // the real product image is hidden. Once we reach t=1 we hand off to the
    // real product image, which scrolls naturally with its section. Reverses
    // when the user scrolls back up into the trigger range.
    let handedOff = null;
    function setHandoff(state) {
      if (state === handedOff) return;
      handedOff = state;
      if (state) {
        ghost.style.display = 'none';
        productBottle.style.opacity = '1';
      } else {
        ghost.style.display = '';
        productBottle.style.opacity = '0';
      }
    }

    function paint(t) {
      if (t >= 1) {
        setHandoff(true);
        return;
      }
      setHandoff(false);
      const h = heroBottle.getBoundingClientRect();
      const p = productBottle.getBoundingClientRect();
      const x = h.left + (p.left - h.left) * t;
      const y = h.top + (p.top - h.top) * t;
      const w = h.width + (p.width - h.width) * t;
      const ht = h.height + (p.height - h.height) * t;
      ghost.style.transform = 'translate3d(' + x + 'px, ' + y + 'px, 0)';
      ghost.style.width = w + 'px';
      ghost.style.height = ht + 'px';
    }

    paint(0);

    const trigger = ScrollTrigger.create({
      id: 'bottle-fly',
      trigger: '.hero-video',
      start: 'top top',
      endTrigger: productSection,
      end: 'top center',
      scrub: true,
      invalidateOnRefresh: true,
      // Repaint at the current progress whenever ScrollTrigger refreshes
      // (resize, reload, font swap, ResizeObserver). Without this the ghost
      // can be left at stale coordinates when layout shifts but scroll
      // progress hasn't changed (e.g. user resizes while parked at top).
      onRefresh: function (self) {
        paint(self.progress);
      },
      onUpdate: function (self) {
        paint(self.progress);
      },
      // Fallbacks: onUpdate stops firing once the trigger is past its end /
      // before its start, so make sure the handoff state is correct when the
      // user crosses the boundary in either direction (or lands there on load).
      onLeave: function () {
        setHandoff(true);
      },
      onEnterBack: function () {
        setHandoff(false);
      },
    });

    // Hero copy fades out on the way out, fades back in on scroll-up (scrub handles reverse)
    const heroCopy = document.querySelector('.hero-video .text-content--inner');
    if (heroCopy) {
      gsap.to(heroCopy, {
        opacity: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: '.hero-video',
          start: 'top top',
          end: 'bottom 70%',
          scrub: true,
        },
      });
    }

    // Product chrome (title, price, ATC, etc.) fades in as the bottle arrives
    const productChrome = productSection.querySelector('.featured-product-form');
    if (productChrome) {
      gsap.from(productChrome, {
        opacity: 0,
        y: 24,
        ease: 'none',
        scrollTrigger: {
          trigger: productSection,
          start: 'top 85%',
          end: 'top 35%',
          scrub: true,
        },
      });
    }

    // When parked at the very top, force the hero back to its fully-visible
    // state. Scrubbed tweens only re-apply their value on a scroll tick, so a
    // refresh while stationary at the top (e.g. the mobile address bar sliding
    // back in) can otherwise leave the copy and bottle stuck mid-animation.
    function atTop() {
      return (window.pageYOffset || document.documentElement.scrollTop || 0) <= 1;
    }
    function forceTopState() {
      if (!atTop()) return;
      paint(0);
      if (heroCopy) gsap.set(heroCopy, { opacity: 1 });
    }

    // Coalesce layout-change signals into a single refresh per frame. Refresh
    // recomputes start/end positions and (via onRefresh above) repaints the
    // ghost at the current progress against the new bounding boxes.
    let pendingRefresh = false;
    function scheduleRefresh() {
      if (pendingRefresh) return;
      pendingRefresh = true;
      requestAnimationFrame(function () {
        pendingRefresh = false;
        ScrollTrigger.refresh();
        forceTopState();
      });
    }

    // Only refresh on a genuine width change (real layout / orientation change).
    // A height-only resize on mobile is the address bar showing/hiding — those
    // must not trigger a refresh, or scrubbed tweens get stranded at the top.
    let lastWidth = window.innerWidth;
    window.addEventListener('resize', function () {
      if (window.innerWidth === lastWidth) {
        // Address-bar-only change: don't refresh, just keep the top state sane.
        forceTopState();
        return;
      }
      lastWidth = window.innerWidth;
      scheduleRefresh();
    });
    window.addEventListener('orientationchange', function () {
      lastWidth = window.innerWidth;
      scheduleRefresh();
    });
    window.addEventListener('load', scheduleRefresh);
    if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(scheduleRefresh);
    }

    // Element-level reflow (image swap, container resize, hero text wrapping
    // changing height) won't fire window 'resize'. Observe the actual nodes.
    if (typeof ResizeObserver === 'function') {
      const ro = new ResizeObserver(scheduleRefresh);
      ro.observe(heroBottle);
      ro.observe(productBottle);
      const heroSection = document.querySelector('.hero-video');
      if (heroSection) ro.observe(heroSection);
      ro.observe(productSection);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
